import api from '../ApiService';
import type { PassportSubjectPreview } from './passport';
import type { PassportVisual } from '@bilt/domain/passportVisual';
export interface CardDesign {
  visual?: PassportVisual;
  template: 'build' | 'event' | 'club' | 'place';
  finish: 'matte' | 'silver' | 'foil';
  accent: 'orange' | 'silver' | 'violet';
  layout: 'portrait' | 'full_bleed' | 'split';
  story: string;
  crop: number;
  art_id: string;
  back_art_id?: string;
  logo_art_id?: string;
  back_image_url?: string | null;
  logo_url?: string | null;
  image_url?: string | null;
}
export interface CardRules {
  evidence: 'qr' | 'location' | 'staff';
  cap: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  venue_name?: string;
  place_id?: string;
  lat?: number | null;
  lng?: number | null;
  radius_m?: number | null;
}
export interface PassportCard {
  preview_context?: 'owner' | 'public';
  editions?: PassportCard[];
  base_key?: string;
  earned_snapshot?: {
    title: string;
    captured_at: string;
    facts?: Record<string, string | number | null>;
  } | null;
  revision?: number;
  template_version_id?: number | null;
  id: string | null;
  key?: string;
  title: string;
  issuer: string;
  subject_type: string;
  subject_id: number;
  status: string;
  design: CardDesign;
  rules: CardRules;
  subject: PassportSubjectPreview | null;
  ordinal?: number | null;
  earned_at?: string;
  evidence?: string;
  ownership_status?: string;
  issued?: number;
  remaining?: number | null;
  version_id?: number | null;
  encounter_id?: string;
  issuance_id?: string;
  is_new?: boolean;
  owned?: boolean;
}
export interface CardHub {
  next_set?: { title: string; slug: string; percent_complete: number; missing: number } | null;
  recent?: PassportCard[];
  cards: PassportCard[];
  showcase: PassportCard[];
  stats: Record<string, number>;
  next_offset: number | null;
  preferences: { showcase: string[]; quick_reveal: boolean; haptics_enabled?: boolean };
}
export const defaultCardDesign: CardDesign = {
  template: 'build',
  finish: 'matte',
  accent: 'orange',
  layout: 'portrait',
  story: '',
  crop: 50,
  art_id: '',
};
export async function cardRequest<T>(
  action: string,
  body?: unknown,
  query: Record<string, string> = {}
): Promise<T> {
  const generation = api.sessionGeneration;
  const response = await api.makeRequest<T>(
    `/passport_cards.php?${new URLSearchParams({ action, ...query })}`,
    body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) }
  );
  if (generation !== api.sessionGeneration) throw new Error('Session changed');
  if (!response.success || response.data === undefined) {
    const error = new Error(response.error || 'Passport is unavailable. Please retry.');
    if ((response as { code?: string }).code === 'cards_unavailable')
      error.name = 'PassportCardsPaused';
    throw error;
  }
  return response.data;
}
export function cardSubjectPath(card: PassportCard): string | null {
  const subject = card.subject;
  if (!subject) return null;
  if (card.subject_type === 'vehicle' && subject.owner_username && subject.public_slug)
    return `/${encodeURIComponent(subject.owner_username)}/${encodeURIComponent(subject.public_slug)}`;
  const root: Record<string, string> = {
    event: '/events/',
    club: '/clubs/',
    shop: '/shops/',
    place: '/places/',
  };
  return root[card.subject_type] && subject.public_slug
    ? root[card.subject_type] + encodeURIComponent(subject.public_slug)
    : null;
}
export function cardEvidenceLabel(value?: string): string {
  if (value === 'location' || value === 'proximity_verified') return 'Location checked';
  if (value === 'staff' || value === 'staff_verified') return 'Confirmed by staff';
  if (value === 'event_verified') return 'Event verified';
  if (value === 'offline_pending') return 'Waiting for verification';
  return 'QR collected';
}
